import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { MagnifyingGlass, Play, RadioButton, CircleNotch, Waveform } from "@phosphor-icons/react";
import api from "../lib/api";
import { useGospelRadio } from "../context/GospelRadioContext";

// Browse (Featured) + search picker for Gospel Radio. Rendered once at
// the app root; opened from the Gospel Music button and the Home chip.
export default function GospelRadioSheet() {
  const { pickerOpen, closePicker, play, current, isPlaying } = useGospelRadio();
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const tRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen || featured.length) return;
    setLoading(true);
    api.get("/gospel-radio/stations")
      .then((r) => setFeatured(r.data?.stations || []))
      .catch(() => setFeatured([]))
      .finally(() => setLoading(false));
  }, [pickerOpen, featured.length]);

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    const term = q.trim();
    if (!term) { setResults(null); setSearching(false); return; }
    setSearching(true);
    tRef.current = setTimeout(() => {
      api.get(`/gospel-radio/search?q=${encodeURIComponent(term)}`)
        .then((r) => setResults(r.data?.stations || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 450);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [q]);

  const list = results !== null ? results : featured;

  const Row = ({ s, ctx }) => {
    const active = current?.id === s.id;
    return (
      <button
        onClick={() => { play(s, ctx); closePicker(); }}
        data-testid={`gospel-station-${s.id}`}
        className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${active ? "bg-forest/10" : "hover:bg-cream-200"}`}
      >
        <span className="shrink-0 w-10 h-10 rounded-lg bg-forest/10 text-forest grid place-items-center overflow-hidden">
          {s.favicon
            ? <img src={s.favicon} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            : <RadioButton size={18} weight="fill" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-forest font-semibold text-sm truncate">{s.name}</div>
          <div className="text-muted_ink text-xs truncate">{[s.country, s.codec, s.bitrate ? `${s.bitrate}kbps` : ""].filter(Boolean).join(" · ")}</div>
        </div>
        {active && isPlaying
          ? <Waveform size={18} weight="fill" className="text-terracotta shrink-0" />
          : <Play size={16} weight="fill" className="text-forest/60 shrink-0" />}
      </button>
    );
  };

  return (
    <Dialog open={pickerOpen} onOpenChange={(o) => { if (!o) closePicker(); }}>
      <DialogContent className="max-w-lg" data-testid="gospel-radio-sheet">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-forest flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Gospel Radio · Live
          </DialogTitle>
        </DialogHeader>
        <p className="text-muted_ink text-xs -mt-1">Live gospel & worship stations — plays in the background with your screen off. Nothing to download.</p>
        <div className="relative mt-2">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted_ink" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search gospel stations…"
            data-testid="gospel-radio-search"
            className="w-full h-11 pl-9 pr-4 rounded-full bg-cream-50 border border-cream-300 outline-none focus:border-forest text-sm"
          />
        </div>
        <div className="mt-2 max-h-[55vh] overflow-y-auto space-y-1 pr-1" data-testid="gospel-radio-list">
          {results === null && <div className="text-[10px] uppercase tracking-[0.16em] text-terracotta font-bold px-1 py-1">Featured</div>}
          {(loading || searching) && (
            <div className="flex items-center justify-center py-8 text-muted_ink"><CircleNotch size={22} className="animate-spin" /></div>
          )}
          {!loading && !searching && list.map((s) => <Row key={s.id} s={s} ctx={list} />)}
          {!loading && !searching && list.length === 0 && (
            <p className="text-muted_ink text-sm py-6 text-center">No stations found. Try another search.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
